import { t } from "@lingui/macro";
import { AnalyzeResumeResult, seniorityLevel } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  ArrowRight,
  Building2,
  TrendingUp,
  Lightbulb,
  Target,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";

type DomainIndustryExperienceProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const DomainIndustryExperience = ({
  analysesData,
  className,
}: DomainIndustryExperienceProps) => {
  const { isDarkMode } = useTheme();
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [showAllTransferable, setShowAllTransferable] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Initial number of items to show
  const INITIAL_MATCHES_TO_SHOW = 4;
  const INITIAL_TRANSFERABLE_TO_SHOW = 3;
  const INITIAL_MISSING_TO_SHOW = 3;

  // Return null if data doesn't exist
  if (!analysesData.metrics.die || !analysesData.metrics.die.object) {
    return null;
  }

  const {
    jd_domains,
    resume_domains,
    resume_positions,
    skill_transferability,
    resume_seniority,
    jd_seniority,
  } = analysesData.metrics.die.object;

  // Get overall score - same calculation as other metric components
  const score =
    analysesData.metrics.die.weight > 0
      ? analysesData.metrics.die.weightedScore / analysesData.metrics.die.weight
      : 0;

  // Define colors - unified with other metric components
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    red: isDarkMode ? "#ef5350" : "#e57373",
    amber: isDarkMode ? "#ff9800" : "#f57c00",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 152, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on score
  const industryPercentage = Math.round(score * 100);
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (industryPercentage >= 80) status = "excellent";
  else if (industryPercentage >= 60) status = "good";

  // Get status information
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`Your industry experience aligns very well with this job.`,
          action: t`Your background is a strong match - emphasize this in your application!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`You have relevant experience but there's room for improvement.`,
          action: t`Review the suggestions below to strengthen your industry alignment.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`Your industry experience may not align well with this job's requirements.`,
          action: t`Focus on highlighting transferable skills and any relevant experience.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Calculate domain matches
  const matchingDomains = jd_domains.filter((domain: string) =>
    resume_domains.some(
      (resumeDomain: string) => resumeDomain.toLowerCase() === domain.toLowerCase(),
    ),
  );

  const missingDomains = jd_domains.filter(
    (domain: string) =>
      !resume_domains.some(
        (resumeDomain: string) => resumeDomain.toLowerCase() === domain.toLowerCase(),
      ),
  );

  // Get high-confidence transferable skills
  const topTransferableSkills = skill_transferability
    .filter((skill) => skill.transferability_strength === "high")
    .slice(0, 8); // Show max 8 total transferable skills

  // Calculate relevant experience
  const relevantExperience = resume_positions
    .filter((pos) =>
      matchingDomains.some((domain: string) => pos.domain.toLowerCase() === domain.toLowerCase()),
    )
    .reduce((sum: number, pos) => sum + pos.years, 0);

  // Seniority comparison
  const seniorityDiff = resume_seniority - jd_seniority;
  const resumeSeniorityText =
    seniorityLevel[resume_seniority as keyof typeof seniorityLevel] || t`Level ${resume_seniority}`;
  const jobSeniorityText =
    seniorityLevel[jd_seniority as keyof typeof seniorityLevel] || t`Level ${jd_seniority}`;

  // Format domain for display
  const formatDomain = (domain: string) => {
    return domain
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Format years
  const formatYears = (years: number) => {
    if (years === 0) return t`0 years`;
    if (years === 1) return t`1 year`;
    return t`${years} years`;
  };

  // Prepare data for display with show more/less functionality
  const matchesToShow = showAllMatches
    ? matchingDomains
    : matchingDomains.slice(0, INITIAL_MATCHES_TO_SHOW);
  const transferableToShow = showAllTransferable
    ? topTransferableSkills
    : topTransferableSkills.slice(0, INITIAL_TRANSFERABLE_TO_SHOW);
  const missingToShow = showAllMissing
    ? missingDomains
    : missingDomains.slice(0, INITIAL_MISSING_TO_SHOW);

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <Info className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {statusInfo.text} ({industryPercentage}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Industries to Highlight */}
        {missingDomains.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                {t`Industries to Highlight`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Consider emphasizing any experience you have in these areas, even if indirect.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {missingToShow.map((domain: string) => (
                <div
                  key={domain}
                  className="flex items-center gap-x-2 rounded-full px-3 py-1 text-sm"
                  style={{ backgroundColor: colors.amberBg, color: colors.amber }}
                >
                  <span>{formatDomain(domain)}</span>
                </div>
              ))}
            </div>

            {missingDomains.length > INITIAL_MISSING_TO_SHOW && (
              <button
                onClick={() => setShowAllMissing(!showAllMissing)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllMissing ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${missingDomains.length - INITIAL_MISSING_TO_SHOW} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Seniority Comparison */}
        <div>
          <div className="mb-3">
            <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
              <Target className="h-5 w-5 text-purple-600" />
              {t`Experience Level`}
            </h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t`How your seniority level compares to what the job requires.`}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
            <div className="mb-3 grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-xs text-slate-500">{t`Your Level`}</p>
                <p className="font-medium">{resumeSeniorityText}</p>
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-500">{t`Required Level`}</p>
                <p className="font-medium">{jobSeniorityText}</p>
              </div>
            </div>

            <div className="flex items-center gap-x-2 text-sm">
              {seniorityDiff > 0 && (
                <>
                  <CheckCircle2 className="h-4 w-4" style={{ color: colors.green }} />
                  <span style={{ color: colors.green }}>
                    {t`You exceed the required seniority level`}
                  </span>
                </>
              )}
              {seniorityDiff === 0 && (
                <>
                  <CheckCircle2 className="h-4 w-4" style={{ color: colors.green }} />
                  <span style={{ color: colors.green }}>
                    {t`Your seniority level matches perfectly`}
                  </span>
                </>
              )}
              {seniorityDiff < 0 && (
                <>
                  <AlertTriangle className="h-4 w-4" style={{ color: colors.amber }} />
                  <span style={{ color: colors.amber }}>
                    {t`You may be under the required seniority level`}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Matching Industries */}
        {matchingDomains.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {t`Your Industry Matches`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Industries where your experience aligns with the job requirements.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {matchesToShow.map((domain: string) => (
                <div
                  key={domain}
                  className="flex items-center gap-x-2 rounded-full px-3 py-1 text-sm"
                  style={{ backgroundColor: colors.greenBg, color: colors.green }}
                >
                  <CheckCircle2 className="h-3 w-3" />
                  <span>{formatDomain(domain)}</span>
                </div>
              ))}
            </div>

            {matchingDomains.length > INITIAL_MATCHES_TO_SHOW && (
              <button
                onClick={() => setShowAllMatches(!showAllMatches)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllMatches ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${matchingDomains.length - INITIAL_MATCHES_TO_SHOW} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Transferable Skills */}
        {topTransferableSkills.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <Lightbulb className="h-5 w-5 text-blue-600" />
                {t`Your Transferable Skills`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`Skills from your background that apply well to this new industry or role.`}
              </p>
            </div>
            <div className="space-y-3">
              {transferableToShow.map((skill, index) => (
                <div
                  key={index}
                  className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
                >
                  <p className="mb-1 text-sm font-medium text-blue-800 dark:text-blue-200">
                    {skill.transferable_skill}
                  </p>
                  <p className="mb-2 text-xs text-blue-700 dark:text-blue-300">
                    {t`From ${formatDomain(skill.source_domain)} → ${formatDomain(skill.target_domain)}`}
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{skill.explanation}</p>
                </div>
              ))}
            </div>

            {topTransferableSkills.length > INITIAL_TRANSFERABLE_TO_SHOW && (
              <button
                onClick={() => setShowAllTransferable(!showAllTransferable)}
                className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {showAllTransferable ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    {t`Show Less`}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    {t`Show ${topTransferableSkills.length - INITIAL_TRANSFERABLE_TO_SHOW} More`}
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
